import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { FormattedMessage, Link, useIntl } from 'gatsby-plugin-intl'
import translate from 'src/intl/translate'
import FilterListIcon from '@material-ui/icons/FilterList'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import CircularProgress from '@material-ui/core/CircularProgress'
import Pagination from 'src/components/Pagination'
import Select from 'src/components/filters/SelectBox'
import { Checkbox as FilterCheckbox } from 'src/components/filters/components/atoms'
import { breakpoint } from 'src/theme/grid'
import { MAX_RESERVATIONS_COUNT_FOR_QUEUE } from 'src/ducks/consts'
import { OFFER_TYPES } from 'src/features/offers/duck/consts'
import { createOfferDetailsLink } from 'src/features/dashboard/duck/consts'
import {
  Arrow,
  BottomRow,
  Company,
  Container,
  DateTime,
  Ellipsis,
  EmptyOffers,
  HeaderItem,
  LocationFrom,
  LocationTo,
  TableHeader,
  TableRow
} from 'src/features/stocks/components/atoms'
import {
  Container as ContainerArea,
  OfferNumber,
  Shipowner
} from 'src/features/myOffers/components/atoms'
import ContainerIcon from 'src/assets/icons/container.svg'
import ShipIcon from 'src/assets/icons/ship.svg'
import CompanyIcon from 'src/assets/icons/work-case.svg'
import ArrowIcon from 'src/assets/icons/arrow-down.svg'
import {
  EXCHANGE_MAP_TYPES,
  getRoundTripOfferKey
} from 'src/features/stocks/components/map/offerMapUtils'
import useSelectedOfferPage from 'src/features/stocks/components/map/useSelectedOfferPage'
import RoundTripRefreshNotice from 'src/features/stocks/components/map/RoundTripRefreshNotice'
import { OfferAgeFilter } from 'src/features/stocks/components/filters/OfferAgeFilter'
import {
  getOfferDateRangeFilters,
  getOfferDateRangeFromFilters
} from 'src/features/stocks/components/filters/offerAgeFilter'

const TYPE_LABELS = {
  [EXCHANGE_MAP_TYPES.EXPORT]: 'offers.mapRoundTripExport',
  [EXCHANGE_MAP_TYPES.GENERAL]: 'offers.mapRoundTripGeneral'
}

const HEADERS = [
  'stocksHeaders.nr',
  'stocksHeaders.loading',
  'stocksHeaders.return',
  'stocksHeaders.container',
  'stocksHeaders.shipper',
  'stocksHeaders.company',
  'offers.mapRoundTripDistance',
  'stocksHeaders.reservations'
]

const RoundTripOffersList = ({
  sourceOffer,
  matches,
  liveOfferTimes,
  filters,
  candidateTypes,
  shipownerOptions,
  containerOptions,
  loading,
  error,
  failedExchangeTypes,
  refreshStatus,
  isDrawerOpen,
  selectedOfferId,
  selectedOfferType,
  onFiltersChange,
  onOffersLoad,
  onOfferSelect
}) => {
  const intl = useIntl()
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [distanceDraft, setDistanceDraft] = useState(filters.maxDistanceKm)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const distanceTimeout = useRef(null)
  const pagesCount = Math.max(1, Math.ceil(matches.length / pageSize))
  const currentPage = Math.min(page, pagesCount)
  const displayedMatches = matches.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  useEffect(() => {
    setPage(1)
  }, [filters, sourceOffer && sourceOffer.id])

  useSelectedOfferPage({
    isDrawerOpen,
    selectedOfferKey:
      selectedOfferId == null
        ? null
        : `${selectedOfferType}:${selectedOfferId}`,
    matches,
    pageSize,
    setPage
  })

  useEffect(() => () => window.clearTimeout(distanceTimeout.current), [])

  useEffect(() => {
    setDistanceDraft(filters.maxDistanceKm)
  }, [filters.maxDistanceKm])

  const updateFilters = nextFilters => {
    window.clearTimeout(distanceTimeout.current)
    const appliedFilters = {
      ...nextFilters,
      maxDistanceKm:
        nextFilters.maxDistanceKm === filters.maxDistanceKm
          ? distanceDraft
          : nextFilters.maxDistanceKm
    }
    onFiltersChange(appliedFilters)
    onOffersLoad(sourceOffer, appliedFilters)
  }

  const handleDistanceChange = value => {
    setDistanceDraft(value)
    window.clearTimeout(distanceTimeout.current)
    distanceTimeout.current = window.setTimeout(() => {
      updateFilters({ ...filters, maxDistanceKm: value })
    }, 350)
  }

  const toggleExchangeType = exchangeType => {
    const exchangeTypes = filters.exchangeTypes.includes(exchangeType)
      ? filters.exchangeTypes.filter(type => type !== exchangeType)
      : [...filters.exchangeTypes, exchangeType]
    updateFilters({ ...filters, exchangeTypes })
  }

  const handleRowKeyDown = (event, match) => {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onOfferSelect(match)
    }
  }

  let listStatus = null
  if (loading) {
    listStatus = (
      <ListStatus
        role='status'
        aria-live='polite'
        aria-label={intl.formatMessage({ id: 'common.loading' })}
      >
        <CircularProgress size={28} />
      </ListStatus>
    )
  } else if (error) {
    listStatus = (
      <ListStatus role='alert'>
        <FormattedMessage id='offers.mapRoundTripLoadError' />
      </ListStatus>
    )
  } else if (matches.length === 0) {
    listStatus = (
      <RoundTripEmptyOffers>
        <FormattedMessage id='offers.mapRoundTripEmpty' />
      </RoundTripEmptyOffers>
    )
  }

  return (
    <ListContainer>
      <FiltersBar>
        <FiltersToggle
          type='button'
          aria-expanded={filtersExpanded}
          onClick={() => setFiltersExpanded(expanded => !expanded)}
        >
          <FilterListIcon aria-hidden='true' />
          <FormattedMessage id='common.filters' />
          <FilterChevron expanded={filtersExpanded} aria-hidden='true' />
        </FiltersToggle>
      </FiltersBar>
      {filtersExpanded && (
        <FiltersPanel>
          <FiltersRow>
            <FilterGroup>
              <FilterGroupLabel>
                <FormattedMessage id='offersFilters.shipowner' />
              </FilterGroupLabel>
              <FilterSelect
                id='reservation-round-trip-shipowner'
                name='shipowner'
                label={intl.formatMessage({ id: 'offersFilters.shipowner' })}
                value={filters.shipowner || []}
                options={shipownerOptions}
                onChange={(name, value) =>
                  updateFilters({ ...filters, [name]: value })
                }
                placeholder={intl.formatMessage({
                  id: 'offersFilters.searchPlaceholder'
                })}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterGroupLabel>
                <FormattedMessage id='orders.containerType' />
              </FilterGroupLabel>
              <FilterSelect
                id='reservation-round-trip-container'
                name='container'
                label={intl.formatMessage({ id: 'orders.containerType' })}
                value={filters.container || []}
                options={containerOptions}
                onChange={(name, value) =>
                  updateFilters({ ...filters, [name]: value })
                }
                placeholder={intl.formatMessage({
                  id: 'offersFilters.searchPlaceholder'
                })}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterGroupLabel>
                {translate(intl.locale).formatMessage({
                  id: 'offersFilters.createdAtWindow'
                })}
              </FilterGroupLabel>
              <AddedFilter
                id='reservation-round-trip-added'
                alignRight
                range={getOfferDateRangeFromFilters(filters)}
                onChange={range =>
                  updateFilters({
                    ...filters,
                    ...getOfferDateRangeFilters(range)
                  })
                }
              />
            </FilterGroup>
          </FiltersRow>
          <FiltersRow>
            <FilterGroup>
              <FilterGroupLabel>
                {intl.formatMessage({
                  id: 'reservations.roundTripOfferKinds',
                  defaultMessage: 'Offer categories'
                })}
              </FilterGroupLabel>
              <FilterChoices>
                {candidateTypes.map(exchangeType => {
                  const selected = filters.exchangeTypes.includes(exchangeType)
                  return (
                    <FilterChoice key={exchangeType}>
                      <input
                        type='checkbox'
                        checked={selected}
                        onChange={() => toggleExchangeType(exchangeType)}
                      />
                      <StyledFilterCheckbox selected={selected} />
                      {intl.formatMessage({ id: TYPE_LABELS[exchangeType] })}
                    </FilterChoice>
                  )
                })}
              </FilterChoices>
            </FilterGroup>
            <FilterGroup>
              <FilterGroupLabel>
                <FormattedMessage id='offers.mapRoundTripDay' />
              </FilterGroupLabel>
              <FilterChoices>
                <FilterChoice>
                  <input
                    type='checkbox'
                    checked={filters.daysAhead === 1}
                    onChange={event =>
                      updateFilters({
                        ...filters,
                        daysAhead: event.target.checked ? 1 : 0
                      })
                    }
                  />
                  <StyledFilterCheckbox selected={filters.daysAhead === 1} />
                  <FormattedMessage id='offers.mapRoundTripNextDay' />
                </FilterChoice>
              </FilterChoices>
            </FilterGroup>
            <FilterGroup>
              <FilterGroupLabel>
                <FormattedMessage id='offers.mapRoundTripRange' />
              </FilterGroupLabel>
              <DistanceControl>
                <DistanceButton
                  type='button'
                  disabled={distanceDraft <= 0}
                  aria-label={intl.formatMessage({
                    id: 'reservations.roundTripDecreaseRadius',
                    defaultMessage: 'Decrease radius'
                  })}
                  onClick={() => handleDistanceChange(distanceDraft - 25)}
                >
                  −
                </DistanceButton>
                <DistanceValue>{distanceDraft} km</DistanceValue>
                <DistanceButton
                  type='button'
                  disabled={distanceDraft >= 300}
                  aria-label={intl.formatMessage({
                    id: 'reservations.roundTripIncreaseRadius',
                    defaultMessage: 'Increase radius'
                  })}
                  onClick={() => handleDistanceChange(distanceDraft + 25)}
                >
                  +
                </DistanceButton>
              </DistanceControl>
            </FilterGroup>
          </FiltersRow>
        </FiltersPanel>
      )}
      <Container>
        <RoundTripHeader numberOfHeaders={HEADERS.length}>
          {HEADERS.map((header, index) => (
            <HeaderItem sort={false} key={`${header}-${index}`}>
              <Ellipsis>
                <FormattedMessage id={header} defaultMessage=' ' />
              </Ellipsis>
            </HeaderItem>
          ))}
        </RoundTripHeader>
        <RoundTripRefreshNotice
          failedExchangeTypes={failedExchangeTypes}
          {...refreshStatus}
        />
        {listStatus}
        {!loading &&
          displayedMatches.map(match => {
            const reservationsCount = Math.max(
              0,
              Number(match.offer.reservationsCount) || 0
            )
            const hasReservations =
              match.offer.type === OFFER_TYPES.QUEUE && reservationsCount > 0

            return (
              <RoundTripRow
                key={`${match.exchangeType}:${match.id}`}
                numberOfHeaders={HEADERS.length}
                isNewLive={Boolean(liveOfferTimes[getRoundTripOfferKey(match)])}
                isSelected={
                  isDrawerOpen &&
                  selectedOfferType === match.exchangeType &&
                  String(selectedOfferId) === String(match.id)
                }
                role='button'
                tabIndex={0}
                onClick={() => onOfferSelect(match)}
                onKeyDown={event => handleRowKeyDown(event, match)}
              >
                <OfferNumber>
                  <Link
                    onClick={event => event.stopPropagation()}
                    to={createOfferDetailsLink(match.referenceNumber)}
                  >
                    {match.referenceNumber}
                  </Link>
                </OfferNumber>
                <LocationFrom>
                  <Arrow>
                    <ArrowIcon style={{ transform: 'rotate(270deg)' }} />
                  </Arrow>
                  <Ellipsis>{match.from.location?.name || '—'}</Ellipsis>
                  <DateTime>{match.from.dateTime}</DateTime>
                </LocationFrom>
                <LocationTo>
                  <Ellipsis>{match.to.location?.name || '—'}</Ellipsis>
                  <DateTime>{match.to.dateTime}</DateTime>
                </LocationTo>
                <ContainerArea>
                  <ContainerIcon />
                  <div>
                    <Ellipsis>{match.containerName || '—'}</Ellipsis>
                  </div>
                </ContainerArea>
                <Shipowner>
                  <ShipIcon />
                  <div>
                    <Ellipsis>{match.shipownerName || '—'}</Ellipsis>
                  </div>
                </Shipowner>
                <Company>
                  <CompanyIcon />
                  <div>
                    <Ellipsis>{match.companyName || '—'}</Ellipsis>
                  </div>
                </Company>
                <DistanceCell>
                  {Math.round(match.roundTripDistanceKm)} km
                </DistanceCell>
                <ReservationsCell>
                  {hasReservations && (
                    <ReservationsValue
                      aria-label={`${intl.formatMessage({
                        id: 'stocksHeaders.reservations'
                      })}: ${reservationsCount}/${MAX_RESERVATIONS_COUNT_FOR_QUEUE}`}
                    >
                      {reservationsCount}/{MAX_RESERVATIONS_COUNT_FOR_QUEUE}
                    </ReservationsValue>
                  )}
                </ReservationsCell>
              </RoundTripRow>
            )
          })}
      </Container>
      {matches.length > 0 && !loading && (
        <BottomRow>
          <Pagination
            itemsPerPage={pageSize}
            currentPage={currentPage}
            pagesCount={pagesCount}
            setPage={setPage}
            setItemsPerPage={limit => {
              setPageSize(Number(limit))
              setPage(1)
            }}
          />
        </BottomRow>
      )}
    </ListContainer>
  )
}

const ListContainer = styled.section`
  width: 100%;
  min-width: 0;
  padding: 0 1rem 1rem;
  box-sizing: border-box;

  ${breakpoint.m`
    padding-right: 1.75rem;
    padding-left: 1.75rem;
  `}
`

const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  min-height: 43px;
  margin: 0 -1rem 0.7rem;
  padding: 0.25rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.4).hex()};
  background: ${({ theme }) => theme.colors.catskillWhite.alpha(0.42).hex()};
  box-sizing: border-box;

  ${breakpoint.m`
    margin-right: -1.75rem;
    margin-left: -1.75rem;
    padding-right: 1.75rem;
    padding-left: 1.75rem;
  `}
`

const FiltersToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.45rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.colors.white.hex()};
  }
`

const FilterChevron = styled(KeyboardArrowDownIcon)`
  margin-left: 0.25rem;
  transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'none')};
  transition: transform 0.15s ease;
`

const FiltersPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: -0.7rem -1rem 1rem;
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.5).hex()};
  background: ${({ theme }) => theme.colors.catskillWhite.alpha(0.24).hex()};

  ${breakpoint.m`
    margin-right: -1.75rem;
    margin-left: -1.75rem;
    padding-right: 1.75rem;
    padding-left: 1.75rem;
  `}
`

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(185px, 1fr));
  gap: 1rem;
`

const FilterGroup = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.45rem;
`

const FilterSelect = styled(Select)`
  width: 100%;

  > div:first-child {
    width: 100%;
  }
`

const AddedFilter = styled(OfferAgeFilter)`
  width: 100%;

  > div:first-child {
    width: 100%;
  }
`

const FilterGroupLabel = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.7rem;
  font-weight: 700;
`

const FilterChoices = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`

const FilterChoice = styled.label`
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }

  input:focus-visible + span {
    box-shadow: 0 0 0 2px
      ${({ theme }) => theme.colors.easternBlue.alpha(0.25).hex()};
  }
`

const StyledFilterCheckbox = styled(FilterCheckbox)`
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  margin-right: 0;
`

const DistanceControl = styled.div`
  display: inline-flex;
  height: 34px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.65).hex()};
  border-radius: 0;
  background: ${({ theme }) => theme.colors.white.hex()};
`

const DistanceButton = styled.button`
  width: 34px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  cursor: pointer;
  font-size: 1.2rem;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: ${({ theme }) => theme.colors.catskillWhite.hex()};
  }

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`

const DistanceValue = styled.span`
  display: inline-flex;
  min-width: 74px;
  align-items: center;
  justify-content: center;
  border-right: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.35).hex()};
  border-left: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.35).hex()};
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.75rem;
  font-weight: 700;
`

const RoundTripHeader = styled(TableHeader)`
  ${breakpoint.m`
    grid-template-areas: 'index locationFrom locationTo container shipowner company date reservations';
    grid-template-columns: minmax(110px, 1fr) repeat(2, minmax(0, 1.55fr)) minmax(80px, 0.85fr) minmax(70px, 0.75fr) minmax(80px, 0.9fr) minmax(64px, 0.65fr) minmax(95px, 1fr);
  `}
`

const RoundTripRow = styled(TableRow)`
  > div:last-child {
    display: flex;
    height: auto;
    overflow: visible;
  }

  grid-template-rows: repeat(4, auto);
  grid-template-areas:
    'index index reservations reservations'
    'locationFrom locationFrom locationTo locationTo'
    'container shipowner company company'
    'date date date date';

  ${breakpoint.m`
    grid-template-rows: none;
    grid-template-areas: 'index locationFrom locationTo container shipowner company date reservations';
    grid-template-columns: minmax(110px, 1fr) repeat(2, minmax(0, 1.55fr)) minmax(80px, 0.85fr) minmax(70px, 0.75fr) minmax(80px, 0.9fr) minmax(64px, 0.65fr) minmax(95px, 1fr);
  `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.easternBlue.hex()};
    outline-offset: -2px;
  }
`

const DistanceCell = styled.div`
  grid-area: date;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.75rem;
  ${breakpoint.m`
    grid-area: auto;
  `}
`

const ReservationsCell = styled.div`
  grid-area: reservations;
  align-items: center;
  justify-content: flex-end;

  ${breakpoint.m`
    justify-content: flex-start;
  `}
`

const ReservationsValue = styled.span`
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
`

const ListStatus = styled.div`
  display: flex;
  min-height: 12vh;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  text-align: center;
`

const RoundTripEmptyOffers = styled(EmptyOffers)`
  min-height: 12vh;
  padding: 0.75rem 1rem;
  box-sizing: border-box;
  text-align: center;
`

export default RoundTripOffersList
